import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | active | pending | failed
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      setAttempts(tries);
      try {
        await refresh();
        const { data } = await api.get("/auth/me");
        if (cancelled) return;
        if (data.has_active_subscription) {
          setStatus("active");
          toast.success("Subscription active — let's train");
          setTimeout(() => nav(data.profile_complete ? "/app/dashboard" : "/onboarding"), 1500);
          return;
        }
        if (tries < 12) {
          setTimeout(poll, 2500);
        } else {
          setStatus("pending");
        }
      } catch {
        if (tries < 12) setTimeout(poll, 2500);
        else setStatus("failed");
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#070707] text-white flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="max-w-md w-full bg-[#0a0a0a] border border-[#1f1f1f] p-8 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-[#d7ff00] mx-auto animate-spin" />
            <h1 className="mt-6 font-display text-3xl uppercase">Confirming <span className="text-[#d7ff00]">payment</span></h1>
            <p className="mt-2 text-sm text-[#bbb]">HitPay is notifying our server — usually takes a few seconds.</p>
            <p className="mt-4 font-mono-jp text-[10px] uppercase tracking-widest text-[#666]">Check {attempts}/12</p>
          </>
        )}
        {status === "active" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-[#d7ff00] mx-auto" />
            <h1 className="mt-6 font-display text-3xl uppercase">Payment <span className="text-[#d7ff00]">confirmed</span></h1>
            <p className="mt-2 text-sm text-[#bbb]">Welcome to Coach JP. Redirecting you to the app…</p>
          </>
        )}
        {status === "pending" && (
          <>
            <Loader2 className="w-12 h-12 text-[#ff9500] mx-auto" />
            <h1 className="mt-6 font-display text-3xl uppercase">Still <span className="text-[#ff9500]">processing</span></h1>
            <p className="mt-2 text-sm text-[#bbb]">HitPay hasn't confirmed yet. You'll receive an email + access will unlock automatically as soon as the payment clears (usually within a minute).</p>
            <button onClick={() => nav("/")} className="mt-6 bg-[#111] border border-[#222] hover:border-[#d7ff00] px-5 py-2.5 font-mono-jp text-xs uppercase tracking-widest" data-testid="back-home">Back to home</button>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle className="w-12 h-12 text-[#ff3b30] mx-auto" />
            <h1 className="mt-6 font-display text-3xl uppercase">Couldn't confirm</h1>
            <p className="mt-2 text-sm text-[#bbb]">If you completed payment, contact Coach JP — we'll activate your account manually.</p>
          </>
        )}
      </div>
    </div>
  );
}
